/* eslint-disable css-modules/no-unused-class */
import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import A from 'wanderu-component-react-router-link';
import {
    Card,
    Grid,
    GridItem,
    Features,
    Image,
    Typography,
    MarkdownContent,
    Section,
} from 'wanderu-components';

import localize from 'wanderu-i18n/src/localize';

import cgConfig from '../../../../config/carrierguide.locale';

import styles from './AboutCarrier.scss';

const AWARD_BADGE_SRC = 'https://static.wanderu.com/carriers/carrier-awards/wanderu-PCA-badge.jpg';
const AWARD_PAGE_URL = 'https://www.wanderu.com/blog/passengers-choice-awards/';

function PhoneNumber(props) {
    const {
        carrier,
        t,
    } = props;
    const phoneNumbers = carrier.contact.phone;

    if (!phoneNumbers) {
        return null;
    }

    const phonePrefix = `${t('contactPhoneSubtitle')} `;

    return Object.entries(phoneNumbers).map(([countryCode, phone]) => (
        <A
            className={styles.carrierPhone}
            href={`tel:${phone}`}
            key={countryCode}
            rel="nofollow"
            underline="none"
            native
        >
            {`${phonePrefix}${countryCode}: ${phone}`}
        </A>
    ));
}

PhoneNumber.propTypes = {
    carrier: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const LocalizedPhoneNumber = localize(PhoneNumber, cgConfig.namespace);

function EmailAddress(props) {
    const {
        carrier,
        t,
    } = props;
    const email = carrier.contact.email;

    if (!email) {
        return null;
    }

    const emailPrefix = `${t('contactEmailSubtitle')} `;

    return (
        <A
            className={styles.carrierEmail}
            href={`mailto:${email}`}
            native
        >
            {`${emailPrefix}${email}`}
        </A>
    );
}

EmailAddress.propTypes = {
    carrier: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const LocalizedEmailAddress = localize(EmailAddress, cgConfig.namespace);

const WebAddress = (props) => {
    const {
        carrier = {},
        t,
    } = props;
    const { name, contact } = carrier;
    const url = contact?.web?.contactPage;

    if (!url) {
        return null;
    }

    return (
        <Typography className={styles.carrierEmail}>
            {t('contact-web-subtitle')}
            {' '}
            <A
                componentProps={{
                    rel: 'nofollow',
                    target: '_blank',
                }}
                href={url}
                native
            >
                {t('contact-web-address', {
                    carrierName: name,
                })}
            </A>
        </Typography>
    );
};

WebAddress.propTypes = {
    carrier: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const LocalizedWebAddress = localize(WebAddress, 'baseApp');

function ContactInfo(props) {
    const {
        carrier,
        t,
    } = props;

    return (
        <div>
            <Typography
                className={`${styles.subTitle} ${styles.contactTitle}`}
                component="h3"
                variant="subtitle"
            >
                {t('contactTitle')}
            </Typography>
            <LocalizedPhoneNumber carrier={carrier} />
            <LocalizedWebAddress carrier={carrier} />
            <LocalizedEmailAddress carrier={carrier} />
        </div>
    );
}

ContactInfo.propTypes = {
    carrier: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const LocalizedContactInfo = localize(ContactInfo, cgConfig.namespace);

function AboutCarrier(props) {
    const {
        i18nMoney,
        i18nNumber,
        t,
        carrier,
        stats = {},
        images = [],
        awardWinningCategories,
    } = props;
    const isWanderuPartner = !!carrier.id;

    function getAboutCarrierTextWithStats(withStats) {
        return t('aboutCarrierText', {
            carrier: carrier.name,
            cities: t.plural('city', withStats.metrics.cities, {
                number: i18nNumber(withStats.metrics.cities).format(),
            }),
            countries: t.plural('country', withStats.metrics.countries, {
                number: i18nNumber(withStats.metrics.countries).format(),
            }),
            maxPrice: i18nMoney(withStats.pricing.maxUsd).format(), // reported price in reported currency - not user's preference here
            minPrice: i18nMoney(withStats.pricing.minUsd).format(),
            mode: carrier.mode,
            routes: i18nNumber(withStats.metrics.routes).format(),
            tripsPerDay: i18nNumber(withStats.metrics.tripsPerDay).format(),
            tripsPerMonth: i18nNumber(withStats.metrics.tripsPerMonth).format(),
        });
    }

    function getAboutCarrierTextWithoutStats() {
        return t('aboutCarrierText', {
            carrier: carrier.name,
            mode: carrier.mode,
        });
    }

    function hasStats(statsCarrier) {
        return statsCarrier
            && statsCarrier.pricing
            && statsCarrier.metrics
            && statsCarrier.pricing.maxUsd
            && statsCarrier.pricing.minUsd;
    }

    const showAwardBadge = !!awardWinningCategories
        && t('awardWinningCategories') !== 'awardWinningCategories';

    return (
        <Section aria-labelledby="cg-about-carrier-title-section">
            <div
                className={classNames(styles.aboutSection, {
                    [styles.hasAward]: showAwardBadge,
                })}
            >
                <div>
                    <Typography
                        className={styles.aboutCarrier}
                        component="h2"
                        id="cg-about-carrier-title-section"
                        variant="h3"
                    >
                        {t('aboutCarrier')}
                    </Typography>
                    <Typography component={MarkdownContent}>
                        {hasStats(stats)
                            ? getAboutCarrierTextWithStats(stats)
                            : getAboutCarrierTextWithoutStats()}
                    </Typography>
                    {isWanderuPartner && (
                        <LocalizedContactInfo
                            carrier={carrier}
                        />
                    )}
                </div>
                {showAwardBadge && (
                    <Card
                        body={(
                            <Typography
                                className={styles.awardBadgeDescription}
                                component={MarkdownContent}
                            >
                                {t('awardWinningCategories')}
                            </Typography>
                        )}
                        className={styles.awardBadgeCard}
                        component={GridItem}
                        componentProps={{
                            xs: 12,
                            md: 4,
                            align: 'start',
                            justify: 'start',
                        }}
                        imageComponent={(
                            <A
                                href={AWARD_PAGE_URL}
                                target="_blank"
                            >
                                <Image
                                    alt={t('awardWinningCategories')}
                                    height={252}
                                    href={AWARD_PAGE_URL}
                                    loading="lazy"
                                    src={AWARD_BADGE_SRC}
                                    width={250}
                                />
                            </A>
                        )}
                        variant="floating"
                    />
                )}
            </div>
            {!!images.length && (
                <>
                    <Typography
                        className={`${styles.subTitle} ${styles.galleryTitle}`}
                        component="h3"
                        variant="subtitle"
                    >
                        {t('carrierPhotos', { carrierName: carrier.name })}
                    </Typography>
                    <Grid
                        className={styles.imageGrid}
                        rowSpacing={2}
                        autoRows
                    >
                        {images.map(({ alt, src, metadata }) => (
                            <Card
                                component={GridItem}
                                componentProps={{
                                    xs: 12,
                                    md: 4,
                                    align: 'start',
                                    justify: 'start',
                                }}
                                imageComponent={(
                                    <Image
                                        alt={alt}
                                        captionLicense={t('stationImageLicense')}
                                        captionTitle={t('stationImageCredit')}
                                        className={styles.carrierImage}
                                        height={192}
                                        loading="lazy"
                                        metadata={metadata}
                                        src={src}
                                        width={343}
                                    />
                                )}
                                key={src}
                                variant="floating"
                            />
                        ))}
                    </Grid>
                </>
            )}
            <Typography
                className={`${styles.subTitle} ${styles.amenitiesTitle}`}
                component="h3"
                variant="subtitle"
            >
                {t('carrierAmenities', { carrierName: carrier.name })}
            </Typography>
            <Features
                className={styles.amenitiesContainer}
                featureNameStyle={styles.featureName}
                features={carrier.features}
                showTooltips={false}
                showFeatureNameNextToIcon
            />
        </Section>
    );
}

AboutCarrier.propTypes = {
    awardWinningCategories: PropTypes.string,
    carrier: PropTypes.object.isRequired,
    i18nMoney: PropTypes.func.isRequired,
    i18nNumber: PropTypes.func.isRequired,
    images: PropTypes.arrayOf(PropTypes.object),
    stats: PropTypes.object,
    t: PropTypes.func.isRequired,
};

AboutCarrier.defaultProps = {
    awardWinningCategories: '',
};

export {
    AboutCarrier,
    ContactInfo,
    EmailAddress,
    PhoneNumber,
};
export default localize(AboutCarrier, cgConfig.namespace);
